import { action, makeObservable, observable } from "mobx";
import { FailureEventArgs } from "@syncfusion/ej2-react-grids";
import { DataManager } from "@syncfusion/ej2-data";
import GridColumn from "../../components/Grids/GridColumns/GridColumn";
import GridODataV4Adaptor from "../../components/Grids/GridOData/GridODataV4Adaptor";
import AppToasterV2 from "../../components/ToastV2/ToastV2";
import { GridActionFailureError } from "../../components/Grids/GridOData/GridActionFailureError";
import { ODataV4BeforeSendArgs } from "./types";

export class ODataStore {
  public authRequestHeader?: Record<string, string>;
  public failureTimeoutCount: number = 0;
  public getAuthRequestHeader?: () => Promise<
    Record<string, string> | undefined
  >;

  constructor() {
    makeObservable(this, {
      authRequestHeader: observable,
      getAuthRequestHeader: observable,
      failureTimeoutCount: observable,
      setFailureTimeoutCount: action,
      setAuthRequestHeader: action,
    });
  }

  public init = async (
    getAuthRequestHeader: () => Promise<Record<string, string> | undefined>
  ) => {
    this.getAuthRequestHeader = getAuthRequestHeader;
    const token = await this.getAuthRequestHeader();
    this.setAuthRequestHeader(token);
  };

  public setAuthRequestHeader = (
    authRequestHeader: Record<string, string> | undefined
  ) => {
    this.authRequestHeader = authRequestHeader;
  };

  public setupODataSource = (
    odataUrl: string,
    columnsConfiguration?: GridColumn[],
    beforeSendFn?: (
      beforeSendArgs: ODataV4BeforeSendArgs
    ) => [string, Record<string, string> | undefined]
  ): DataManager => {
    const dataManager = new DataManager({
      url: odataUrl,
      adaptor: new GridODataV4Adaptor(
        columnsConfiguration,
        beforeSendFn ? beforeSendFn : this.baseOnBeforeSend
      ),
      crossDomain: true,
    });

    return dataManager;
  };

  public setFailureTimeoutCount = (value: number) => {
    this.failureTimeoutCount = value;
  };

  protected baseActionCompleteEventHandler = () => {
    this.setFailureTimeoutCount(0);
  };

  protected baseActionFailureEventHandler = async (
    eventArgs: FailureEventArgs,
    refreshFn: () => void
  ) => {
    // Process Error
    const errorStatus =
      (eventArgs.error as unknown as XMLHttpRequest)?.status ||
      (eventArgs.error as unknown as GridActionFailureError)?.error.status ||
      0;
    if (!!eventArgs.error && [401, 403].includes(errorStatus)) {
      this.setFailureTimeoutCount(this.failureTimeoutCount + 1);

      //eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.failureTimeoutCount;

      if (this.failureTimeoutCount < 3) {
        if (this.getAuthRequestHeader) {
          const token = await this.getAuthRequestHeader();
          this.setAuthRequestHeader(token);
        }

        refreshFn();
      } else {
        AppToasterV2.showFailedActionToaster(
          "Authorization Error. Please refresh and try again."
        );
      }
    } else {
      AppToasterV2.showFailedActionToaster(
        "An error occurred executing the current action."
      );
    }
  };

  // Base Methods
  protected baseOnBeforeSend = ({
    queryString,
  }: ODataV4BeforeSendArgs): [string, Record<string, string> | undefined] => {
    return [queryString, this.authRequestHeader];
  };

  public baseActionCompleteEvent = () => {
    return () => {
      this.baseActionCompleteEventHandler();
    };
  };

  public baseActionFailureEvent = (refreshFn: () => void) => {
    return async (eventArgs: FailureEventArgs) => {
      // Process Error
      await this.baseActionFailureEventHandler(eventArgs, refreshFn);
    };
  };
}

export const odataStore = new ODataStore();
